import { apiFetch } from "../api.js";

/**
 * Portfolio api-module — Phase 11 (2026-05-09).
 *
 * Web does NOT import from @hearth/shared (5.1 lock + 8.2 server-only-
 * subpath refinement); we keep parallel browser-safe types here.
 */

export type ConcentrationSeverity = "ok" | "warning" | "critical";

export interface ConcentrationRow {
  ticker: string;
  value_cents: number;
  pct_of_portfolio: number;
  severity: ConcentrationSeverity;
}

export interface ConcentrationResponse {
  total_value_cents: number;
  warn_pct: number;
  rows: ConcentrationRow[];
  warning_count: number;
  critical_count: number;
  rule_of_thumb: string;
}

export interface OverlapRow {
  ticker: string;
  direct_value_cents: number;
  indirect_value_cents: number;
  effective_value_cents: number;
  effective_pct: number;
  indirect_sources: Array<{ fund_ticker: string; value_cents: number }>;
}

export interface OverlapResponse {
  total_value_cents: number;
  rows: OverlapRow[];
  diversified_residual_cents: number;
  overlap_tickers: string[];
  data_as_of: string;
  rule_of_thumb: string;
}

export interface SectorRollupRow {
  key: string;
  value_cents: number;
  pct: number;
}

export interface SectorExposureResponse {
  total_value_cents: number;
  by_sector: SectorRollupRow[];
  by_region: SectorRollupRow[];
  diversified_residual_cents: number;
  sector_taxonomy: string;
  data_as_of: string;
}

export function getConcentration(): Promise<ConcentrationResponse> {
  return apiFetch<ConcentrationResponse>("/v1/portfolio/concentration");
}

export function getOverlap(): Promise<OverlapResponse> {
  return apiFetch<OverlapResponse>("/v1/portfolio/overlap");
}

export function getSectorExposure(): Promise<SectorExposureResponse> {
  return apiFetch<SectorExposureResponse>("/v1/portfolio/sectors");
}

// ─── Cleave 2 ─────────────────────────────────────────────────────────────

export interface ExpenseRatioRow {
  ticker: string;
  value_cents: number;
  expense_ratio_bp: number;
  est_annual_drag_cents: number;
  recommended_etf_equivalent?: string;
  est_annual_drag_if_switched_cents?: number;
  est_annual_savings_if_switched_cents?: number;
}

export interface ExpenseRatiosResponse {
  total_value_cents: number;
  total_annual_drag_cents: number;
  weighted_avg_expense_ratio_bp: number;
  rows: ExpenseRatioRow[];
  total_potential_annual_savings_cents: number;
  unknown_tickers: string[];
  data_as_of: string;
  rule_of_thumb: string;
}

export type TaxEfficiencyClass = "etf_low" | "index_mutual_med" | "active_high";

export interface TaxCostRow {
  ticker: string;
  value_cents: number;
  is_taxable_account: boolean;
  tax_efficiency_class: TaxEfficiencyClass;
  est_annual_distribution_cents: number;
  est_annual_tax_drag_cents: number;
  est_annual_tax_cost_ratio_bp: number;
  recommended_etf_equivalent?: string;
  est_annual_drag_if_switched_cents?: number;
  est_annual_savings_if_switched_cents?: number;
}

export interface TaxCostResponse {
  total_value_cents: number;
  total_annual_tax_drag_cents: number;
  weighted_avg_tax_cost_ratio_bp: number;
  rows: TaxCostRow[];
  total_potential_annual_savings_cents: number;
  data_as_of: string;
  rule_of_thumb: string;
}

export type BenchmarkKey =
  | "sp500"
  | "total_us_market"
  | "total_world"
  | "sixty_forty"
  | "custom_pct";

export type BenchmarkKind = "actual_etf" | "historical_real";

export interface BenchmarkResponse {
  portfolio: {
    return_pct: number;
    period_days: number;
    total_cashflows_cents: number;
    annualized_pct: number;
  };
  benchmark_key: BenchmarkKey;
  // Phase 16d additions:
  benchmark_kind?: BenchmarkKind;
  benchmark_period_pct?: number;
  etf_ticker?: string | null;
  benchmark_bars?: number;
  benchmark_annualized_pct: number;
  excess_pct: number;
  excess_cents_at_end?: { amount_cents: number; note: string };
  period_days: number;
  cashflows_attributed: number;
  methodology: string;
  error_code?: string;
  message?: string;
  // Phase 16e additions — basis selection + per-account scope + extra
  // return metrics for the /portfolio Performance tab card grid.
  basis?: "net_worth" | "portfolio";
  account_id?: string | null;
  total_pct?: number;
  twr_pct?: number;
  irr_pct?: number | null;
  max_drawdown_pct?: number;
}

export function getExpenseRatios(): Promise<ExpenseRatiosResponse> {
  return apiFetch<ExpenseRatiosResponse>("/v1/portfolio/expense-ratios");
}

export function getTaxCost(): Promise<TaxCostResponse> {
  return apiFetch<TaxCostResponse>("/v1/portfolio/tax-cost");
}

export function getBenchmark(args: {
  vs?: BenchmarkKey;
  period_days?: number;
  /**
   * Phase 16d: defaults to `"actual_etf"` server-side. Pass
   * `"historical_real"` to render the informational long-window card.
   */
  kind?: BenchmarkKind;
  /**
   * Phase 16e: defaults to `"portfolio"` (positions-only) server-side.
   * Pass `"net_worth"` to keep the legacy assets-minus-liabilities
   * basis. Mutually exclusive with `account_id` (server validates).
   */
  basis?: "net_worth" | "portfolio";
  /**
   * Phase 16e: scope to one account in the household. Reads
   * `portfolio_by_account[account_id]` from snapshots; queries only
   * that account's transactions for cashflows. Implies
   * `basis=portfolio`.
   */
  account_id?: string;
}): Promise<BenchmarkResponse> {
  const params = new URLSearchParams();
  if (args.vs) params.set("vs", args.vs);
  if (args.period_days)
    params.set("period_days", args.period_days.toString());
  if (args.kind) params.set("kind", args.kind);
  if (args.basis) params.set("basis", args.basis);
  if (args.account_id) params.set("account_id", args.account_id);
  const qs = params.toString();
  return apiFetch<BenchmarkResponse>(
    `/v1/portfolio/benchmark${qs ? `?${qs}` : ""}`,
  );
}

// ─── Cleave 3 ─────────────────────────────────────────────────────────────

export interface RebalanceTrade {
  sleeve: string;
  action: "buy" | "sell" | "hold";
  current_value_cents: number;
  target_value_cents: number;
  delta_cents: number;
  amount_cents: number;
  account_hint?: string;
  skip_reason?: "wash_sale" | "no_inflow_remaining" | "below_min_trade";
}

export interface RebalanceResponse {
  current_total_cents: number;
  post_rebalance_total_cents: number;
  trades: RebalanceTrade[];
  drift_before_pp: number;
  drift_after_pp: number;
  cash_inflow_remaining_cents: number;
  notes: string[];
  target_allocation: Record<string, number>;
  drift_warn_pp: number;
  methodology: string;
}

export type StressScenarioKey =
  | "gfc_2008"
  | "covid_2020"
  | "inflation_2022"
  | "stagflation_70s"
  | "custom";

export interface StressResponse {
  scenario: {
    key: StressScenarioKey;
    label: string;
    equity_delta_pct: number;
    bond_delta_pct: number;
  };
  before_cents: number;
  after_cents: number;
  delta_cents: number;
  delta_pct: number;
  buckets: Array<{
    asset_class: "equity" | "bond" | "other";
    label?: string;
    before_cents: number;
    after_cents: number;
    delta_cents: number;
  }>;
  available_scenarios: Array<{
    key: StressScenarioKey;
    label: string;
    equity_delta_pct: number;
    bond_delta_pct: number;
  }>;
  methodology: string;
}

export function getRebalance(args: {
  cash_inflow_cents?: number;
  allow_sells?: boolean;
}): Promise<RebalanceResponse> {
  const params = new URLSearchParams();
  if (args.cash_inflow_cents !== undefined)
    params.set("cash_inflow_cents", String(args.cash_inflow_cents));
  if (args.allow_sells === true) params.set("allow_sells", "true");
  const qs = params.toString();
  return apiFetch<RebalanceResponse>(
    `/v1/portfolio/rebalance${qs ? `?${qs}` : ""}`,
  );
}

export function getStressTest(args: {
  scenario?: StressScenarioKey;
  equity_pct?: number;
  bond_pct?: number;
}): Promise<StressResponse> {
  const params = new URLSearchParams();
  if (args.scenario) params.set("scenario", args.scenario);
  if (args.equity_pct !== undefined)
    params.set("equity_pct", String(args.equity_pct));
  if (args.bond_pct !== undefined)
    params.set("bond_pct", String(args.bond_pct));
  const qs = params.toString();
  return apiFetch<StressResponse>(
    `/v1/portfolio/stress-test${qs ? `?${qs}` : ""}`,
  );
}

// ─── Cleave 4 ─────────────────────────────────────────────────────────────

export type PayoutFrequency = "quarterly" | "monthly" | "semi_annual" | "annual";

export interface UpcomingPayout {
  ticker: string;
  ex_div_date: string;
  est_payout_cents: number;
  amount_per_share_cents: number;
  payout_frequency: PayoutFrequency;
}

export interface DividendCalendarResponse {
  upcoming: UpcomingPayout[];
  est_annual_income_cents: number;
  est_monthly_income_cents: number;
  per_ticker: Array<{
    ticker: string;
    shares: number;
    est_annual_income_cents: number;
    yield_pct: number;
  }>;
  unknown_tickers: string[];
  window_days: number;
  data_as_of: string;
  methodology: string;
}

export type ActivityEventType =
  | "net_worth_milestone"
  | "fire_progress_milestone"
  | "account_balance_milestone"
  | "first_account"
  | "account_archived"
  | "bill_paid_streak";

export interface ActivityEvent {
  event_id: string;
  event_type: ActivityEventType;
  event_data: Record<string, unknown>;
  occurred_at: string | null;
}

export interface ActivityFeedResponse {
  events: ActivityEvent[];
  count: number;
}

export function getDividends(days = 30): Promise<DividendCalendarResponse> {
  return apiFetch<DividendCalendarResponse>(
    `/v1/portfolio/dividends?days=${days}`,
  );
}

export function getActivityFeed(limit = 20): Promise<ActivityFeedResponse> {
  return apiFetch<ActivityFeedResponse>(
    `/v1/portfolio/activity-feed?limit=${limit}`,
  );
}

// === Phase 13 — contribution priority =====================================

export type ContributionRungLabel =
  | "401k_to_employer_match"
  | "hsa_to_limit"
  | "roth_ira_to_limit"
  | "401k_traditional_to_limit"
  | "mega_backdoor"
  | "taxable";

export interface ContributionRung {
  label: ContributionRungLabel;
  headline: string;
  is_eligible: boolean;
  reason_if_ineligible: string | undefined | null;
  remaining_capacity_cents: number;
  ytd_contributed_cents: number;
  annual_limit_cents: number;
  citation: string;
}

export interface ContributionPriorityResponse {
  tax_year: number;
  limits_as_of: string;
  limits_stale: boolean;
  rungs: ContributionRung[];
  next_dollar: ContributionRung | null;
  generated_at: string;
}

export function getContributionPriority(): Promise<ContributionPriorityResponse> {
  return apiFetch<ContributionPriorityResponse>(
    "/v1/portfolio/contribution-priority",
  );
}
